import Vue from "vue";
import VideoModel, {create as createVideo} from '../models/VideoModel';
import {INPUT_STATE_OK, INPUT_STATE_ERROR, INPUT_STATE_SAVING} from '../components/uploader/input-state.vue';
import { mapApiErrorsToModelErrors } from '../models/Model';
import ApiService from '../lib/api';

function handleAPIErrors(commit, id, errors) {
  for (const i in errors) {
    commit('setError', {
      id: id,
      key: errors[i].field,
      value: errors[i].message
    });
  }
}

function validatePayloadID(payload) {
  if (typeof payload.id !== 'number' || parseInt(payload.id) !== payload.id) {
    throw new Error('Invalid ID in payload.');
  }
}

function validatePayloadKey(payload) {
  if (typeof payload.key !== 'string') {
    throw new Error('Invalid key in payload.');
  }
}

function validatePayloadValue(payload) {
  if (typeof payload.value === 'undefined') {
    throw new Error('Value must be defined in payload.');
  }
}

function validatePayload(payload) {
  validatePayloadID(payload);
  validatePayloadKey(payload);
  validatePayloadValue(payload);
}

/**
 *
 * @param data
 * @returns {{data: *, state: {}, errors: {}}}
 */
// function createVideo1(data) {
//   return new VideoModel(data);
//   let errors = {};
//   if (data._embedded && data._embedded.errors) {
//     for (const i in data._embedded.errors) {
//       const err = data._embedded.errors[i];
//       errors[err.field] = err.message;
//     }
//     delete data._embedded.errors;
//   }
//
//   if (data._embedded) {
//     delete data._embedded;
//   }
//   return {
//     data: data,
//     state: {},
//     errors: errors,
//     upload: {
//       file: null,
//       progress: 0
//     }
//   };
// }

function mergeVideo(a, b) {
  for (const i in b.data) {
    if (i === '_embedded') {
      continue;
    }

    a.data[i] = b.data[i];
  }

  Vue.set(a, 'errors', {});
  for (const i in b.errors) {
    a.errors[i] = b.errors[i];
  }

  if (Object.keys(b.errors).length < 1) {
    Vue.set(a, 'errors',  {}); // TODO errors don't work
  }
}

const state = {
  videos: [],
  focused: 0,
};

const getters = {
  all: (state) => {
    return state.videos;
  },
  incomplete: (state) => {
    return state.videos.filter(v => v.isIncomplete()).sort((a, b) => a.data.id > b.data.id ? -1 : 1);
  },
  complete: (state) => {
    return state.videos.filter(v => v.isComplete()).sort((a, b) => a.data.id > b.data.id ? -1 : 1);
  },
  converting: (state) => {
    return state.videos.filter(v => v.isConverting()).sort((a, b) => a.data.id > b.data.id ? -1 : 1);
  },
  uploading: (state) => {
    return state.videos.filter(v => v.isUploading()).sort((a, b) => a.data.id > b.data.id ? -1 : 1);
  },
  focused: (state, getters) => {
    return getters.byId(state.focused);
  },
  byId: (state) => (id) => {
    return state.videos.filter(v => v.data.id === id)[0] || false;
  },
  videoIsIncomplete: (state, getters) => id => {
    return ['pending_info', 'incomplete'].indexOf(getters.byId(id).data.approved) > -1;
  },
  videoIsUploading: (state, getters) => id => {
    const video = getters.byId(id);
    return video.upload.upload_progress > 0 && video.upload.upload_progress < 100 || video.data.source_file_status === 'uploading';
  }
};

const actions = {
  fetchIncomplete({commit, dispatch, getters}) {
    return new Promise((resolve, reject) => {
      ApiService.get('/videos/mine/incomplete').then((resp) => {
        let promises = [];
        for (const i in resp.data) {
          promises.push(dispatch('add', resp.data[i]));
        }

        Promise.all(promises).then(() => {
          if (!getters.focused) {
            dispatch('refocus');
          }
          resolve();
        });
      }).catch(reject);
    });
  },
  fetchComplete({commit, dispatch, getters}) {
    return new Promise((resolve, reject) => {
      ApiService.get('/videos/mine').then((resp) => {
        let promises = [];
        for (const i in resp.data) {
          promises.push(dispatch('add', resp.data[i]));
        }

        Promise.all(promises).then(() => {
          if (!getters.focused) {
            dispatch('refocus');
          }
          resolve();
        });
      }).catch(reject);
    });
  },
  fetchById({commit, dispatch}, id) {
    return new Promise((resolve, reject) => {
      ApiService.get('/videos/' + id).then((resp) => {
        dispatch('add', resp.data);
        resolve(resp.data.id);
      }).catch(reject);
    });
  },
  update({commit}, payload) {
    validatePayload(payload);

    const localOnly = payload.local || false;

    if (!localOnly) {
      commit('updateState', {
        id: payload.id,
        key: payload.key,
        value: INPUT_STATE_SAVING,
      });
    }

    commit('update', payload);

    if (localOnly) {
      return new Promise(resolve => resolve());
    }

    return new Promise((resolve, reject) => {
      ApiService.put('/videos/' + payload.id + '/' + payload.key, {
        value: payload.value
      }).then((resp) => {
        commit('clearError', {
          id: payload.id,
          key: payload.key,
        });

        commit('updateState', {
          id: payload.id,
          key: payload.key,
          value: INPUT_STATE_OK,
        });

        resolve();
      }).catch((resp) => {
        if (resp.json.errors) {
          handleAPIErrors(commit, payload.id, resp.json.errors);
        }

        commit('updateState', {
          id: payload.id,
          key: payload.key,
          value: INPUT_STATE_ERROR,
        });

        reject();
      });
    });
  },
  add({commit, getters}, data) {
    return new Promise((resolve, reject) => {
      if (!getters.byId(data.id)) {
        let errors;
        if (data?._embedded?.errors) {
          errors = mapApiErrorsToModelErrors(data._embedded.errors);
        }
        commit('add', createVideo(data, errors));
        resolve();
      }
    });
  },
  focus({commit, getters}, id) {
    commit('focus', getters.byId(id) ? id : 0);
  },
  setFile({commit}, payload) {
    validatePayloadID(payload);
    commit('setFile', payload);
  },
  updateState({commit}, payload) {
    validatePayload(payload);
    commit('updateState', payload);
  },
  updateUploadPurpose({commit, dispatch}, payload) {
    validatePayloadID(payload);

    commit('updateState', {
      id: payload.id,
      key: 'upload_purpose_id',
      value: INPUT_STATE_SAVING,
    });

    commit('update', {
      id: payload.id,
      key: 'upload_purpose_id',
      value: payload.purpose_id,
    });

    commit('update', {
      id: payload.id,
      key: 'upload_purpose_value',
      value: payload.purpose_value,
    });

    return new Promise((resolve, reject) => {
      ApiService.patch('/videos/' + payload.id, {
        upload_purpose_id: payload.purpose_id,
        upload_purpose_value: payload.purpose_value,
        _draft: true,
      }).then((resp) => {
        dispatch('clearError', {
          id: payload.id,
          key: 'upload_purpose_id',
        });

        commit('updateState', {
          id: payload.id,
          key: 'upload_purpose_id',
          value: INPUT_STATE_OK,
        });
      }).catch(() => {
        commit('updateState', {
          id: payload.id,
          key: 'upload_purpose_id',
          value: INPUT_STATE_ERROR,
        });
      });
    });
  },
  setError({commit}, payload) {
    validatePayload(payload);
    commit('setError', payload);
  },
  createFromFile({commit}, payload) {
    return new Promise((resolve, reject) => {
      ApiService.post('/videos', {
        title: payload.name
      }).then(resp => {
        const video = createVideo(resp.data);

        commit('add', video);
        commit('focus', video.data.id);
        resolve(video.data.id);
      }).catch(reject);
    });
  },
  createFromEmbed({commit, dispatch}, payload) {
    return new Promise((resolve, reject) => {
      ApiService.post('/videos').then(resp => {
        resp.data.type = 'embed';
        const video = createVideo(resp.data);
        commit('add', video);
        dispatch('setEmbedSource', {
          id: video.data.id,
          embed: payload,
          overwrite_data: true
        });
        resolve();
      }).catch((resp) => {
        if (resp.json.errors) {
          const errors = resp.json.errors;
          for (const i in errors) {
            commit('setError', {
              id: payload.id,
              key: errors[i].field,
              value: errors[i].message,
            });
          }
        }

        reject(resp);
      });
    });
  },
  /**
   * Sync data between the frontend and backend.
   * @param commit
   * @param getters
   * @param {Number} payload.id
   * @param {Array} payload.keys optional array of keys and errors to sync, ignoring the rest.
   * @returns {Promise<unknown>}
   */
  sync({commit, getters}, payload) {
    validatePayloadID(payload);

    const video = getters.byId(payload.id);

    function updateFromResponse (resp) {
      let data = {};
      if (payload.keys) {
        for (const i in resp.data) {
          if (payload.keys.indexOf(i) > -1) {
            data[i] = resp.data[i];
          }
        }
        if (resp.data._embedded && resp.data._embedded.errors) {
          data._embedded = { errors: {} };
          for (const i in resp.data._embedded.errors) {
            if (payload.keys.indexOf(i) > -1) {
              data._embedded.errors[i] = resp.data._embedded.errors[i];
            }
          }
        }
      } else {
        data = resp.data;
      }

      commit('replace', createVideo(data, resp.errors ? mapApiErrorsToModelErrors(resp.errors) : null));
    }

    return new Promise((resolve, reject) => {
      ApiService.patch('/videos/' + payload.id, video.data).then(resp => {
        updateFromResponse(resp);
        resolve();
      }).catch((resp) => {
        updateFromResponse(resp.json);
        reject();
      });
    });
  },
  syncSourceStatusUntilComplete({commit, dispatch, getters}, id) {
    return new Promise(((resolve, reject) => {
      ApiService.get('/videos/' + id).then(resp => {
        commit('update', {
          id: id,
          updates: [
            {
              key: 'source_file_status',
              value: resp.data.source_file_status
            },
            {
              key: 'thumb',
              value: resp.data.thumb
            },
            {
              key: 'player_image',
              value: resp.data.player_image
            },
            {
              key: 'type',
              value: resp.data.type
            }
          ]
        });

        const video = getters.byId(id);

        if (video.isProcessing()) {
          setTimeout(() => {
            dispatch('syncSourceStatusUntilComplete', id);
          }, 2000);
        }
      });
    }));
  },
  setUploadProgress({commit}, payload) {
    validatePayloadID(payload);
    if (typeof payload.progress !== 'number') {
      throw new Error('Invalid progress in payload.');
    }

    commit('setUploadProgress', payload);
  },
  clearError({commit}, payload) {
    validatePayloadID(payload);
    validatePayloadKey(payload);
    commit('clearError', payload)
  },
  setEmbedSource({commit, dispatch}, payload) {
    validatePayloadID(payload);

    const progressHandler = (i) => {
      dispatch('setUploadProgress', {
        id: payload.id,
        progress: i
      });
    };

    let pos = 1;
    progressHandler(pos);

    const timer = setInterval(() => {
      progressHandler(pos++);
    }, 300);

    return new Promise((resolve, reject) => {
      ApiService.post('/videos/' + payload.id + '/source', {
        embed_url: payload.embed,
        overwrite_data: payload.overwrite_data || false
      }).then(resp => {
        commit('replace', createVideo(resp.data));
        dispatch('clearError', {
          id: payload.id,
          key: 'source_file_status',
        });
        dispatch('clearError', {
          id: payload.id,
          key: 'embed_url',
        });

        if (resp.data._embedded && resp.data._embedded.errors) {
          for (const err of resp.data._embedded.errors) {
            dispatch('setError', {
              id: payload.id,
              key: err.field,
              value: err.message,
            });
          }
        }

        commit('refreshCacheID', payload.id);
        clearInterval(timer);
        progressHandler(100);
        resolve();
      }).catch((resp) => {
        let errorMessage;

        if (!resp.json.errors) {
          errorMessage = 'An unknown error occurred embedding this video.';
        } else {
          const errors = resp.json.errors.filter(a => a.field === 'embed_url');
          errorMessage = errors[0].message;
        }
        if (errorMessage) {
          dispatch('setError', {
            id: payload.id,
            key: 'embed_url',
            value: errorMessage
          });
        }
        clearInterval(timer);
        progressHandler(0);
        reject();
      });
    });
  },
  delete({commit, getters, dispatch}, id) {
    commit('updateState', {
      id: id,
      key: 'deleting',
      value: true
    });
    return new Promise((resolve, reject) => {
      ApiService.delete('/videos/' + id).then(resp => {
        commit('remove', id);
        resolve(id);
      }).catch(reject);
    });
  },
  /**
   * Refocus the form to the latest video
   * @param commit
   */
  refocus({commit, getters, dispatch}) {
    if (getters.incomplete.length > 0) {
      dispatch('focus', getters.incomplete[0].data.id);
    } else if (getters.complete.length > 0) {
      dispatch('focus', getters.complete[0].data.id);
    } else {
      dispatch('focus', 0);
    }
  },
  addInstance({commit}, payload) {
    commit('add', payload);
  }
};

const mutations = {
  add(store, payload) {
    store.videos.push(payload);
  },
  remove(store, payload) {
    for (const i in store.videos) {
      if (store.videos[i].data.id === payload) {
        store.videos.splice(i, 1);
        break;
      }
    }
  },
  /**
   *
   * @param store
   * @param {Number} payload.id
   * @param {Array} payload.updates
   * @param {String} payload.key
   * @param {any} payload.value
   */
  update(store, payload) {
    const videos = store.videos.filter(v => v.data.id === payload.id);
    if (videos.length === 0) {
      throw Error('No video to update.');
    }

    if (payload.updates) {
      for (const i in payload.updates) {
        const update = payload.updates[i];
        validatePayloadKey(update);
        validatePayloadValue(update);

        Vue.set(videos[0].data, update.key, update.value);
      }
    } else {
      Vue.set(videos[0].data, payload.key, payload.value);
    }
  },
  replace(store, payload) {
    store.videos.forEach((item, i) => {
      if (item.data.id === payload.data.id) {
        mergeVideo(store.videos[i], payload);
      }
    });
  },
  updateState(store, payload) {
    const videos = store.videos.filter(v => v.data.id === payload.id);
    if (videos.length === 0) {
      throw Error('No video to update.');
    }
    Vue.set(videos[0].state, payload.key, payload.value);
  },
  setError(store, payload) {
    const videos = store.videos.filter(v => v.data.id === payload.id);
    if (videos.length === 0) {
      throw Error('No video to update.');
    }
    Vue.set(videos[0].errors, payload.key, payload.value);
  },
  clearError(store, payload) {
    const videos = store.videos.filter(v => v.data.id === payload.id);
    if (videos.length === 0) {
      throw Error('No video to update.');
    }

    if (videos[0].errors && videos[0].errors[payload.key]) {
      Vue.delete(videos[0].errors, payload.key);
    }
  },
  focus(store, id) {
    store.focused = id;
  },
  setUploadProgress (store, payload) {
    const videos = store.videos.filter(v => v.data.id === payload.id);
    if (videos.length === 0) {
      throw Error('No video to update.');
    }

    if (videos[0].upload) {
      videos[0].upload.upload_progress = payload.progress;
    }
  },
  setFile(store, payload) {
    const videos = store.videos.filter(v => v.data.id === payload.id);
    if (videos.length === 0) {
      throw Error('No video to update.');
    }

    videos[0].upload.file = payload.file;
  },
  refreshCacheID(store, id) {
    const videos = store.videos.filter(v => v.data.id === id);
    if (videos.length === 0) {
      throw Error('No video to update.');
    }

    videos[0].cacheID = Math.random();
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
